import React, { useCallback, useEffect, useState } from 'react';
import { Container } from '../../components/content';
import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Input,
  Row,
  Select,
  Tooltip,
  Upload,
  Tabs,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import styles from '../../styles/user.module.scss';
import useResize from '../../hooks/useResize';
import { InfoCircleOutlined } from '@ant-design/icons';
import { authClient } from '../../api/client';
import { formatDate } from './../../utils/formatDate';
import { deleteUser, fetchUser, updateUser } from '../../api/user-api';
import AdsTable from '../../components/tables/AdsTable';
import { AuthAPI } from '../../api/auth-api';
import { uploadImage } from '../../api/image-api';
import { fetchUserAds } from '../../api/ad-api';
import DeleteModal from '../../components/modals/DeleteModal';
import { useUserVerification } from '../../hooks/useUserVerification';
import UserVerificationModal from '../../components/modals/UserVerificationModal';
import { validatePhoneNumber } from '../../utils/validatePhoneNumber';
import { userRoleTranslations } from '../../constants/user-constants';
import { usePermissions } from '../../context/PermissionsContext';

const initialFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  role: '',
  companyName: '',
  companyAddress: '',
  responsibleName: '',
  responsiblePosition: '',
};

export default function User() {
  const { can } = usePermissions();
  const { id } = useParams();
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const width = useResize();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [filter, setFilter] = useState();
  const navigate = useNavigate();

  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const [ads, setAds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const {
    userForVerification,
    isUserVerificationModalOpen,
    handleVerifyUser,
    handleVerifyCompanyUser,
    handleVerifyUserModalClose,
  } = useUserVerification({
    onLoadingChange: setLoading,
    onMessageChange: setMessage,
    onModalClose: () => setDone(false),
    onLoadUser: (userId) => {
      loadUser();
    },
    onVerificationComplete: (isDone) => {
      setDone(isDone);
      setTimeout(() => {
        handleVerifyUserModalClose();
      }, 1000);
    },
  });

  const handleModalClose = () => {
    handleVerifyUserModalClose();
    setDone(false);
  };

  const handleSearch = () => {};

  const handleFilterChange = (newFilter) => {
    setFilter((prev) => ({
      ...prev,
      ...newFilter,
    }));
  };

  const getFormData = useCallback(() => {
    return {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      password: user?.passwordToken || '',
      role: user?.role || '',
      companyName: user?.companyName || '',
      companyAddress: user?.companyAddress || '',
      responsibleName: user?.responsibleName || '',
      responsiblePosition: user?.responsiblePosition || '',
    };
  }, [user]);

  // Sync formData with user when user changes
  useEffect(() => {
    if (user) {
      setFormData(getFormData());
    }
  }, [user, getFormData]);

  const loadUser = useCallback(async () => {
    const { data, success, message } = await fetchUser(id);

    if (!success) {
      return;
    }

    setUser(data);
  }, [id]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    if (activeTab !== 'oglasi') return;
    setEditMode(false);

    const loadUserAds = async () => {
      const { data, error } = await fetchUserAds({
        page: currentPage,
        limit: 10,
        filter,
        userId: id,
      });

      if (error) {
        console.log(error);
        return;
      }

      setAds(data.ads);
      setCurrentPage(data.page);
      setTotalPages(data.totalPages);
    };

    loadUserAds();
  }, [activeTab, currentPage, filter, id]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleMenuClick = (info) => {
    if (info.key === '2') setEditMode(true);
    if (info.key === '4') {
      setIsDeleteModalOpen(true);
    }
    if (info.key === 'verify') {
      handleVerifyUser(user._id, user.type, true);
    }
    if (info.key === 'unverify') {
      handleVerifyUser(user._id, user.type, false);
    }
  };

  const handleInputChange = (e) => {
    setLoading(false);
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteUser = async () => {
    const { data, error } = await deleteUser(id);

    if (error) {
      setMessage(error);
    } else {
      setMessage(data.message || 'Korisnik obrisan');
      setTimeout(() => {
        setIsDeleteModalOpen(false);
        navigate('/korisnici');
      }, 1000);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    setLoading(true);

    const {
      user: updatedUser,
      message,
      error,
    } = await updateUser(id, formData, user);

    if (error) {
      setError(error);
      setDone(false);
    } else {
      setUser(updatedUser);
      setMessage(message);
      setError(null);
      setDone(true);
      setTimeout(() => {
        setEditMode(false);
        setDone(false);
      }, 1000);
    }

    setIsLoading(false);
    setLoading(false);
  };

  const handleCancel = () => {
    setFormData(getFormData());
    setEditMode(false);
    setPhoneError(null);
  };

  const handleGoBack = () => {
    window.history.back();
  };

  const handleResetPassword = async (email) => {
    if (!email) return;

    const { success, message } = await AuthAPI.forgotPassword({ email });

    if (!success) {
      setError(message);
      return;
    }

    setMessage(`Zahtev za promenu šifre ponovo poslat na ${email}`);
  };

  const handleImageUpload = async (file, onSuccess, onError) => {
    const { data, error } = await uploadImage(file);

    if (error) {
      setError(error);
      return onError(error);
    }

    const { _id } = data;

    const updateRes = await updateUser(
      user._id,
      {
        profileImage: _id,
      },
      user
    );

    if (updateRes.error) {
      setError(updateRes.error);
      return onError(updateRes.error);
    }

    setUser();

    setMessage('Profilna slika je uspešno postavljena.');
    onSuccess(data);
  };

  return (
    <Container className={styles.user}>
      <Card className="card-gradient">
        <div className={styles.user__goBack}>
          <Button className="btn-tertiary btn-noBorder" onClick={handleGoBack}>
            <img src="/assets/icons/back.svg" alt="Go back" /> Korisnici
          </Button>
        </div>
        <div className={styles.user__header}>
          <h1>{user?.email}</h1>
          {width > 1024 && (
            <Dropdown
              menu={{
                items: [
                  {
                    key: '1',
                    label: 'Akcije',
                    disabled: true,
                  },
                  ...(activeTab === 'info'
                    ? [
                        {
                          key: '2',
                          label: 'Promeni informacije',
                        },
                      ]
                    : []),
                  ...([
                    'pending_verification',
                    'pending_admin_verification',
                  ].includes(user?.status)
                    ? [
                        {
                          key: 'verify',
                          label: 'Verifikuj',
                          icon: (
                            <img
                              src="/assets/icons/verification.svg"
                              alt="Verify"
                            />
                          ),
                        },
                      ]
                    : []),
                  ...(user?.status === 'active'
                    ? [
                        {
                          key: 'unverify',
                          label: 'Ukloni verifikaciju',
                          icon: (
                            <img
                              src="/assets/icons/verification.svg"
                              alt="Verify"
                            />
                          ),
                        },
                      ]
                    : []),
                  ...(can('users:delete')
                    ? [
                        {
                          key: '4',
                          label: 'Obriši korisnika',
                          className: 'delete',
                          icon: (
                            <img src="/assets/icons/delete.svg" alt="Delete" />
                          ),
                        },
                      ]
                    : []),
                ],
                onClick: handleMenuClick,
              }}
              trigger={['click']}
            >
              <Button
                type="primary"
                className={`btn-primary ${styles.user__actions}`}
              >
                Akcije <img src="/assets/icons/arrow-down.svg" alt="Dropdown" />
              </Button>
            </Dropdown>
          )}
        </div>
        <Row className={styles.user__info}>
          <Col>
            <div className={styles.user__infoText}>
              <strong>ID Korisnika</strong> {user?._id}
            </div>
          </Col>
          <Col>
            <div className={styles.user__infoText}>
              <strong>Korisnik registrovan</strong>{' '}
              {formatDate(user?.createdAt)}
            </div>
          </Col>
          <Col>
            <div className={styles.user__infoText}>
              <strong>Verifikacija</strong>{' '}
              {user?.status === 'active'
                ? 'Verifikovan'
                : user?.status === 'rejected'
                  ? 'Verfikacija odbijena'
                  : 'Nije verifikovan'}
            </div>
          </Col>
        </Row>

        {user?.status === 'rejected' && (
          <Row>
            <div className={styles.user__infoText}>
              <strong>Razlog odbijanja</strong> {user?.reasonForRejection}
            </div>
          </Row>
        )}

        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          className={styles.user__tabs}
          items={[
            {
              key: 'info',
              label: 'Lični podaci',
              children: (
                <>
                  <div className={styles.user__contact}>
                    {width < 1024 && (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: '1',
                              label: 'Akcije',
                              disabled: true,
                            },
                            {
                              key: '2',
                              label: 'Promeni informacije',
                            },
                            ...([
                              'pending_verification',
                              'pending_admin_verification',
                            ].includes(user?.status)
                              ? [
                                  {
                                    key: 'verify',
                                    label: 'Verifikuj',
                                    icon: (
                                      <img
                                        src="/assets/icons/verification.svg"
                                        alt="Verify"
                                      />
                                    ),
                                  },
                                ]
                              : []),
                            ...(user?.status === 'active'
                              ? [
                                  {
                                    key: 'unverify',
                                    label: 'Ukloni verifikaciju',
                                    icon: (
                                      <img
                                        src="/assets/icons/verification.svg"
                                        alt="Verify"
                                      />
                                    ),
                                  },
                                ]
                              : []),
                            {
                              key: '4',
                              label: 'Obriši korisnika',
                              className: 'delete',
                              icon: (
                                <img
                                  src="/assets/icons/delete.svg"
                                  alt="Delete"
                                />
                              ),
                            },
                          ],
                          onClick: handleMenuClick,
                        }}
                        trigger={['click']}
                      >
                        <Button
                          type="primary"
                          className={`btn-primary ${styles.user__actions}`}
                        >
                          Akcije{' '}
                          <img
                            src="/assets/icons/arrow-down.svg"
                            alt="Dropdown"
                          />
                        </Button>
                      </Dropdown>
                    )}
                  </div>
                  {error && (
                    <Alert
                      message={error}
                      type="error"
                      showIcon
                      style={{ margin: '16px 0', width: 'fit-content' }}
                    />
                  )}
                  {message && (
                    <Alert
                      message={message}
                      type="success"
                      showIcon
                      style={{ margin: '16px 0', width: 'fit-content' }}
                    />
                  )}
                  <div className={styles.user__personalInfo}>
                    <div className={styles.user__personalInfo__image}>
                      <img
                        style={{
                          padding: user?.profileImage?.url ? '0' : '8px',
                        }}
                        className={styles.user__personalInfo__image__upload}
                        src={
                          user?.profileImage?.url || '/assets/icons/user.svg'
                        }
                        alt={`${user?.firstName} ${user?.lastName}`}
                      />
                      <div className={styles.user__personalInfo__image__wrap}>
                        <Upload
                          accept="image/*"
                          maxCount={1}
                          customRequest={async ({ file, onSuccess, onError }) =>
                            handleImageUpload(file, onSuccess, onError)
                          }
                        >
                          <Button className="btn-tertiary btn-small">
                            Dodaj sliku
                          </Button>
                        </Upload>

                        <span className={styles.user__personalInfo__image__dim}>
                          Preporučena dimenzija 1:1
                        </span>
                      </div>
                    </div>
                    {user?.type === 'individual' ? (
                      <Row gutter={24} className={styles.user__info}>
                        <Col>
                          <div className={styles.user__infoText}>
                            <strong>Ime</strong>{' '}
                            {editMode ? (
                              <Input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleInputChange}
                                className={styles.user__input}
                              />
                            ) : (
                              user?.firstName
                            )}
                          </div>
                        </Col>
                        <Col>
                          <div className={styles.user__infoText}>
                            <strong>Prezime</strong>{' '}
                            {editMode ? (
                              <Input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleInputChange}
                                className={styles.user__input}
                              />
                            ) : (
                              user?.lastName
                            )}
                          </div>
                        </Col>
                      </Row>
                    ) : (
                      <>
                        <Row gutter={24} className={styles.user__info}>
                          <Col>
                            <div className={styles.user__infoText}>
                              <strong>Naziv firme</strong>{' '}
                              {editMode ? (
                                <Input
                                  type="text"
                                  name="companyName"
                                  value={formData.companyName}
                                  onChange={handleInputChange}
                                  className={styles.user__input}
                                />
                              ) : (
                                user?.companyName
                              )}
                            </div>
                          </Col>
                          <Col>
                            <div className={styles.user__infoText}>
                              <strong>PIB</strong> {user?.pib}
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <div className={styles.user__infoText}>
                            <strong>Adresa firme</strong>{' '}
                            {editMode ? (
                              <Input
                                type="text"
                                name="companyAddress"
                                value={formData.companyAddress}
                                onChange={handleInputChange}
                                className={styles.user__input}
                              />
                            ) : (
                              user?.companyAddress
                            )}
                          </div>
                        </Row>
                        <Row gutter={24} className={styles.user__info}>
                          <Col>
                            <div className={styles.user__infoText}>
                              <strong>Ime odgovornog lica</strong>{' '}
                              {editMode ? (
                                <Input
                                  type="text"
                                  name="responsibleName"
                                  value={formData.responsibleName}
                                  onChange={handleInputChange}
                                  className={styles.user__input}
                                />
                              ) : (
                                user?.responsibleName
                              )}
                            </div>
                          </Col>
                          <Col>
                            <div className={styles.user__infoText}>
                              <strong>Pozicija odgovornog lica</strong>{' '}
                              {editMode ? (
                                <Input
                                  type="text"
                                  name="responsiblePosition"
                                  value={formData.responsiblePosition}
                                  onChange={handleInputChange}
                                  className={styles.user__input}
                                />
                              ) : (
                                user?.responsiblePosition
                              )}
                            </div>
                          </Col>
                        </Row>
                      </>
                    )}
                    <Col>
                      <div className={styles.user__password}>
                        <div className={styles.user__infoText}>
                          <strong>
                            Šifra{' '}
                            <Tooltip
                              title={
                                <span>
                                  <b>Šifra mora da sadrži sledeće:</b>
                                  <br />
                                  Minimum 8 karaktera
                                  <br />
                                  Mala i velika slova
                                  <br />
                                  Specijalni karakteri !$%_.,#+
                                </span>
                              }
                            >
                              <InfoCircleOutlined
                                style={{ color: '#6D758F' }}
                              />
                            </Tooltip>
                          </strong>

                          <span>**********</span>
                        </div>
                        {editMode && (
                          <Button
                            className="btn-tertiary"
                            onClick={() => handleResetPassword(user?.email)}
                          >
                            Pošalji link
                          </Button>
                        )}
                      </div>
                    </Col>
                    <Col>
                      <div className={styles.user__infoText}>
                        <strong>Broj telefona</strong>{' '}
                        {editMode ? (
                          <>
                            <Input
                              name="phone"
                              value={formData.phone}
                              onChange={(e) => {
                                handleInputChange(e);
                                validatePhoneNumber(
                                  e.target.value,
                                  setPhoneError
                                );
                              }}
                              className={styles.user__input}
                            />
                            {phoneError && (
                              <div
                                style={{ color: '#c94747', fontSize: '12px' }}
                              >
                                {phoneError}
                              </div>
                            )}
                          </>
                        ) : (
                          user?.phone
                        )}
                      </div>
                    </Col>
                    <Col span={12}>
                      <div className={styles.user__infoText}>
                        <strong>Rola</strong>{' '}
                        {editMode ? (
                          <Select
                            name="role"
                            value={formData?.role || user?.role}
                            className={styles.user__input}
                            onChange={(value) =>
                              setFormData((prev) => ({ ...prev, role: value }))
                            }
                            options={[
                              { value: 'admin', label: 'Admin' },
                              { value: 'client', label: 'Korisnik' },
                              { value: 'doctor', label: 'Mentor' },
                              { value: 'clinic_owner', label: 'Klinika' },
                              { value: 'promoter', label: 'Promoter' },
                              { value: 'agent', label: 'Agent' },
                            ]}
                            style={{ width: 150 }}
                          />
                        ) : (
                          userRoleTranslations[user?.role]
                        )}
                      </div>
                    </Col>
                  </div>
                </>
              ),
            },
            {
              key: 'oglasi',
              label: 'Oglasi',
              children: (
                <AdsTable
                  ads={ads}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  filter={filter}
                  handleSearch={handleSearch}
                  handleFilterChange={handleFilterChange}
                  showAddButton={false}
                />
              ),
            },
          ]}
        />
        {editMode && (
          <div className={styles.user__editActionsWrapper}>
            <div className={styles.user__editActions}>
              Pravite izmene
              <div className={styles.user__editActions__buttons}>
                <Button
                  className={`btn-transparent btn-small ${styles.user__editActions__cancel}`}
                  onClick={handleCancel}
                >
                  Poništi
                </Button>
                <Button
                  type="primary"
                  onClick={handleSave}
                  className={`${styles.user__editActions__save} btn-primary btn-small`}
                  style={{ width: '90px' }}
                  disabled={phoneError}
                >
                  {loading ? (
                    <img
                      src="/assets/icons/add.svg"
                      alt="Add Icon"
                      className="animate-spin"
                    />
                  ) : done ? (
                    <img
                      src="/assets/icons/check.svg"
                      className="check-icon"
                      alt="Check Icon"
                    />
                  ) : (
                    <>Sačuvaj</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>
      <DeleteModal
        visible={isDeleteModalOpen}
        onDelete={handleDeleteUser}
        onCancel={() => setIsDeleteModalOpen(false)}
        loading={loading}
        done={done}
        title="Brisanje korisnika"
        message="Da li ste sigurni da želite da obrišete ovog korisnika?"
        buttonText="Obriši korisnika"
      />
      <UserVerificationModal
        userId={user?._id}
        visible={isUserVerificationModalOpen}
        onClose={handleModalClose}
        onVerify={handleVerifyCompanyUser}
        verifyLoading={loading}
        verifyDone={done}
      />
    </Container>
  );
}
